import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { Link, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { Container } from '../components/Animations'
import Breadcrumbs from '../components/Breadcrumbs'
import { PrimaryButton, SecondaryButton } from '../components/Button'
import ConfirmButtonModal from '../components/ConfirmButtonModal'
import CopyButton from '../components/CopyButton'
import { TextArea } from '../components/Input'
import Markdown from '../components/Markdown'
import Spinner from '../components/Spinner'
import TimeText from '../components/TimeText'
import { ListPage } from '../components/DashboardPrimitives'
import Radom, { ISupportMessage, ISupportTicketDetail } from '../state/Radom'
import { RADOM_COLORS } from '../util/Constants'
import { errorToast, successToast } from '../util/Util'
import { categoryLabel, supportStatusBadge } from './Support'

const HeaderCard = styled.div`
  background-color: white;
  border: 1px solid ${RADOM_COLORS.GRAY_MED};
  border-radius: 12px;
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  flex-wrap: wrap;
`

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`

const TicketSubject = styled.span`
  font-size: 20px;
  font-weight: 500;
  color: ${RADOM_COLORS.BLACK};
  overflow-wrap: anywhere;
`

const TicketIdChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: monospace;
  font-size: 12px;
  color: ${RADOM_COLORS.GRAY_DARKER};
  background-color: ${RADOM_COLORS.GRAY_LIGHTEST};
  border: 1px solid ${RADOM_COLORS.GRAY_MED};
  border-radius: 6px;
  padding: 3px 8px;
  width: fit-content;
`

const TicketMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 13px;
  color: ${RADOM_COLORS.GRAY_DARKER};
  flex-wrap: wrap;
`

const Thread = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 780px;
`

const MessageRow = styled.div<{ $own?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: ${({ $own }) => $own ? 'flex-end' : 'flex-start'};
`

const MessageBubble = styled.div<{ $own?: boolean }>`
  padding: 14px 16px;
  border-radius: 14px;
  border-bottom-right-radius: ${({ $own }) => $own ? '4px' : '14px'};
  border-bottom-left-radius: ${({ $own }) => $own ? '14px' : '4px'};
  max-width: 85%;
  font-size: 14px;
  line-height: 1.55;
  background-color: ${({ $own }) => $own ? RADOM_COLORS.BLACK : 'white'};
  color: ${({ $own }) => $own ? 'white' : RADOM_COLORS.BLACK};
  border: 1px solid ${({ $own }) => $own ? RADOM_COLORS.BLACK : RADOM_COLORS.GRAY_MED};
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
`

const MessageMeta = styled.div`
  display: flex;
  gap: 8px;
  align-items: baseline;
  font-size: 12px;
  color: ${RADOM_COLORS.GRAY_DARKER};
  padding: 0 4px;
`

const AuthorAvatar = styled.span<{ $own?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 600;
  color: white;
  background-color: ${({ $own }) => $own ? RADOM_COLORS.BLACK : RADOM_COLORS.GRAY_DARKER};
`

const DayDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${RADOM_COLORS.GRAY_DARKER};
  font-size: 12px;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: ${RADOM_COLORS.GRAY_MED};
  }
`

const ReplyCard = styled.div`
  background-color: white;
  border: 1px solid ${RADOM_COLORS.GRAY_MED};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 780px;
`

const ReplyHint = styled.span`
  font-size: 12px;
  color: ${RADOM_COLORS.GRAY_DARKER};
`

const EmptyThread = styled.div`
  padding: 30px;
  text-align: center;
  color: ${RADOM_COLORS.GRAY_DARKER};
  font-size: 14px;
`

const authorLabel = (author: string): string => {
  if (author === 'you') return 'You'
  if (author === 'assistant') return 'Radom Assistant'
  return 'Radom Support'
}

const authorInitials = (author: string): string => {
  if (author === 'you') return 'Y'
  return 'R'
}

const dayKey = (iso: string): string => new Date(iso).toDateString()

const SupportTicket = observer(() => {
  const params = useParams()
  const ticketId = params.id ?? ''

  const [detail, setDetail] = useState<ISupportTicketDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [reply, setReply] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [isClosing, setIsClosing] = useState(false)

  const loadTicket = (background = false): void => {
    Radom.getSupportTicket(ticketId)
      .then(res => {
        setDetail(res)
        Radom.refreshSupportUnreadCount()
      })
      .catch(err => {
        if (!background) errorToast(err.response?.data?.error || err.message)
      })
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    setIsLoading(true)
    loadTicket()
    const interval = setInterval(() => {
      if (!document.hidden) loadTicket(true)
    }, 30_000)
    return () => clearInterval(interval)
  }, [ticketId])

  const onSendReply = async (): Promise<void> => {
    if (!reply.trim()) return
    setIsSending(true)
    try {
      await Radom.createSupportMessage(ticketId, reply.trim())
      setReply('')
      successToast('Reply sent')
      loadTicket()
    } catch (err: any) {
      errorToast(err.response?.data?.error || err.message)
    } finally {
      setIsSending(false)
    }
  }

  const onCloseTicket = async (): Promise<void> => {
    setIsClosing(true)
    try {
      await Radom.closeSupportTicket(ticketId)
      successToast('Ticket closed')
      loadTicket()
    } catch (err: any) {
      errorToast(err.response?.data?.error || err.message)
    } finally {
      setIsClosing(false)
    }
  }

  if (isLoading) {
    return <Container>
      <ListPage>
        <Spinner />
      </ListPage>
    </Container>
  }

  if (!detail) {
    return <Container>
      <ListPage>
        <span>Ticket not found. <Link to='/support'>Back to support</Link></span>
      </ListPage>
    </Container>
  }

  const { ticket, messages } = detail
  const isResolved = ticket.status === 'resolved'

  let lastDay = ''

  return <Container>
    <ListPage>
      <Breadcrumbs items={[
        { item: 'Support', to: '/support' },
        { item: ticket.subject, to: '' }
      ]} />

      <HeaderCard>
        <HeaderInfo>
          <TicketSubject>{ticket.subject}</TicketSubject>
          <TicketIdChip>
            {ticket.id}
            <CopyButton text={ticket.id} />
          </TicketIdChip>
          <TicketMeta>
            {supportStatusBadge(ticket.status)}
            <span>{categoryLabel(ticket.category)}</span>
            <span style={{ textTransform: 'capitalize' }}>Opened via {ticket.source}</span>
            <TimeText date={new Date(ticket.createdAt)} />
          </TicketMeta>
        </HeaderInfo>
        {!isResolved && <ConfirmButtonModal
          title='Close ticket'
          description='Are you sure you want to close this ticket? You can always reopen it later by sending another reply.'
          action='Close ticket'
          onConfirm={onCloseTicket}
          ActionComponent={props => <SecondaryButton {...props} isLoading={isClosing}>
            Close ticket
          </SecondaryButton>}
        />}
      </HeaderCard>

      <Thread>
        {messages.map((message: ISupportMessage) => {
          const own = message.author === 'you'
          const day = dayKey(message.createdAt)
          const divider = day !== lastDay
          lastDay = day

          return <React.Fragment key={message.id}>
            {divider && <DayDivider>
              {new Date(message.createdAt).toLocaleDateString(undefined, {
                month: 'long', day: 'numeric', year: 'numeric'
              })}
            </DayDivider>}
            <MessageRow $own={own}>
              <MessageMeta>
                <AuthorAvatar $own={own}>{authorInitials(message.author)}</AuthorAvatar>
                <span style={{ fontWeight: 500 }}>{authorLabel(message.author)}</span>
                <TimeText date={new Date(message.createdAt)} />
              </MessageMeta>
              <MessageBubble $own={own}>
                {own
                  ? <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{message.body}</span>
                  : <Markdown text={message.body} />}
              </MessageBubble>
            </MessageRow>
          </React.Fragment>
        })}
        {messages.length === 0 && <EmptyThread>No messages yet</EmptyThread>}
      </Thread>

      <ReplyCard>
        <TextArea
          rows={4}
          value={reply}
          placeholder={isResolved ? 'Reply to reopen this ticket' : 'Write a reply'}
          onChange={e => setReply(e.target.value)}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <ReplyHint>
            {isResolved
              ? 'This ticket is resolved — replying will reopen it.'
              : 'Our AI assistant replies within minutes; a human follows up when needed.'}
          </ReplyHint>
          <PrimaryButton isLoading={isSending} disabled={!reply.trim()} onClick={() => { onSendReply() }}>
            Send reply
          </PrimaryButton>
        </div>
      </ReplyCard>
    </ListPage>
  </Container>
})

export default SupportTicket
